import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Paper,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button
} from '@mui/material';
import { 
  CheckCircle,
  TrendingUp,
  AccountBalance,
  Home,
  AttachMoney,
  Speed,
  Security,
  Calculate
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const InvestmentBasics = () => {
  const investmentTypes = [
    {
      title: 'Fix and Flip',
      description: 'Purchase distressed properties, renovate them, and sell for profit',
      icon: <Home sx={{ fontSize: 40, color: '#1B5E20' }} />,
      pros: ['Quick returns', 'Active involvement', 'High profit potential'],
      cons: ['Requires capital', 'Market timing risk', 'Renovation expertise needed'],
      timeframe: '3-12 months'
    },
    {
      title: 'Buy and Hold',
      description: 'Acquire properties for long-term rental income and appreciation',
      icon: <AccountBalance sx={{ fontSize: 40, color: '#1B5E20' }} />,
      pros: ['Passive income', 'Tax benefits', 'Long-term appreciation'],
      cons: ['Property management', 'Lower immediate returns', 'Tenant issues'],
      timeframe: '5+ years'
    },
    {
      title: 'Wholesale',
      description: 'Contract properties and assign the contract to another buyer for a fee',
      icon: <Speed sx={{ fontSize: 40, color: '#1B5E20' }} />,
      pros: ['Low capital needed', 'Quick turnaround', 'No renovation required'],
      cons: ['Lower profit margins', 'Requires strong network', 'Deal finding skills'],
      timeframe: '30-90 days'
    },
    {
      title: 'REO Properties',
      description: 'Purchase bank-owned properties after failed foreclosure auctions',
      icon: <Security sx={{ fontSize: 40, color: '#1B5E20' }} />,
      pros: ['Clear title', 'Inspection possible', 'Negotiable terms'],
      cons: ['Higher competition', 'Less discount', 'Bank processes slow'],
      timeframe: '60-120 days'
    }
  ];

  const keyMetrics = [
    {
      name: 'Cap Rate',
      formula: 'Net Operating Income / Property Value',
      description: 'Measures the rate of return on investment property',
      goodRange: '5-10%'
    },
    {
      name: 'Cash-on-Cash Return',
      formula: 'Annual Cash Flow / Total Cash Invested',
      description: 'Measures the cash income earned on the cash invested',
      goodRange: '8-12%'
    },
    {
      name: 'ARV (After Repair Value)',
      formula: 'Comparable Sales + Renovation Value Add',
      description: 'Estimated value of property after renovations',
      goodRange: 'Based on comps'
    },
    {
      name: '70% Rule',
      formula: 'ARV x 70% - Repair Costs = Max Offer',
      description: 'Quick formula for determining maximum purchase price',
      goodRange: 'Investor guideline'
    }
  ];

  const gettingStartedSteps = [
    {
      step: 1,
      title: 'Education & Research',
      description: 'Learn about local markets, foreclosure laws, and investment strategies. Attend seminars and network with experienced investors.'
    },
    {
      step: 2,
      title: 'Set Your Budget',
      description: 'Determine your available capital, financing options, and risk tolerance. Create a realistic investment budget.'
    },
    {
      step: 3,
      title: 'Build Your Team',
      description: 'Connect with real estate attorneys, contractors, inspectors, and property managers before making your first purchase.'
    },
    {
      step: 4,
      title: 'Analyze Properties',
      description: 'Research properties thoroughly. Calculate potential returns, estimate renovation costs, and assess market conditions.'
    },
    {
      step: 5,
      title: 'Make Your First Investment',
      description: 'Start with a manageable property. Apply your research, follow your plan, and learn from the experience.'
    }
  ];

  const commonMistakes = [
    'Overestimating ARV (After Repair Value)',
    'Underestimating renovation costs',
    'Skipping property inspections',
    'Not factoring in holding costs',
    'Emotional decision making',
    'Inadequate market research',
    'Over-leveraging with debt',
    'Ignoring location factors'
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" sx={{ 
            fontWeight: 700, 
            color: '#1B5E20',
            mb: 2 
          }}>
            Investment Basics
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#666',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6
          }}>
            Everything you need to know to start investing in real estate. 
            Learn strategies, metrics, and best practices for successful property investment.
          </Typography>
        </Box>

        {/* Investment Types */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 4, color: '#1B5E20' }}>
            Investment Strategies
          </Typography>
          <Grid container spacing={3}>
            {investmentTypes.map((type, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {type.icon}
                      <Box sx={{ ml: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                          {type.title}
                        </Typography>
                        <Chip label={type.timeframe} size="small" color="primary" sx={{ mt: 0.5 }} />
                      </Box>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
                      {type.description}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                          Pros:
                        </Typography>
                        {type.pros.map((pro, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <CheckCircle sx={{ fontSize: 14, color: '#2e7d32', mr: 0.5 }} />
                            <Typography variant="body2">{pro}</Typography>
                          </Box>
                        ))}
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#d32f2f', mb: 1 }}>
                          Cons:
                        </Typography>
                        {type.cons.map((con, i) => (
                          <Typography key={i} variant="body2" sx={{ mb: 0.5, color: '#666' }}>
                            • {con}
                          </Typography>
                        ))}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Key Metrics */}
        <Paper sx={{ p: 4, mb: 6, bgcolor: '#e8f5e8' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Calculate sx={{ fontSize: 32, color: '#1B5E20', mr: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1B5E20' }}>
              Key Investment Metrics
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {keyMetrics.map((metric, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1B5E20', mb: 1 }}>
                      {metric.name}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontFamily: 'monospace', 
                      bgcolor: '#f5f5f5', 
                      p: 1, 
                      borderRadius: 1,
                      mb: 1 
                    }}>
                      {metric.formula}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                      {metric.description}
                    </Typography>
                    <Chip label={`Target: ${metric.goodRange}`} size="small" variant="outlined" color="success" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Getting Started Steps */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 4, color: '#1B5E20' }}>
            Getting Started: 5 Steps
          </Typography>
          <Grid container spacing={3}>
            {gettingStartedSteps.map((item, index) => (
              <Grid item xs={12} key={index}>
                <Card sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateX(8px)' }
                }}>
                  <Box sx={{ 
                    bgcolor: '#1B5E20', 
                    color: 'white', 
                    width: 60, 
                    minWidth: 60,
                    height: '100%',
                    minHeight: 80,
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {item.step}
                    </Typography>
                  </Box>
                  <CardContent sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Common Mistakes */}
        <Paper sx={{ p: 4, mb: 6, bgcolor: '#fff3e0' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, color: '#e65100' }}>
            Common Mistakes to Avoid
          </Typography>
          <Grid container spacing={2}>
            {commonMistakes.map((mistake, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  bgcolor: 'white',
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid #ffe0b2'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {index + 1}. {mistake}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Success Mindset */}
        <Paper sx={{ p: 4, mb: 6, bgcolor: '#1B5E20', color: 'white' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
            The Investor Mindset
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <TrendingUp sx={{ fontSize: 48, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Think Long-Term
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Real estate is a marathon, not a sprint. Build wealth through consistent, smart decisions.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Security sx={{ fontSize: 48, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Manage Risk
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Never invest more than you can afford to lose. Diversify and always have reserves.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <AttachMoney sx={{ fontSize: 48, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Know Your Numbers
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Every investment decision should be backed by thorough financial analysis.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Call to Action */}
        <Box sx={{ 
          textAlign: 'center', 
          bgcolor: 'white',
          border: '2px solid #1B5E20',
          borderRadius: 3,
          p: 4
        }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#1B5E20' }}>
            Ready to Learn More?
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, color: '#666' }}>
            Explore our other educational resources
          </Typography>
          <Button 
            component={Link}
            to="/foreclosure-guide"
            variant="contained" 
            size="large"
            sx={{ 
              bgcolor: '#1B5E20',
              fontWeight: 600,
              px: 4,
              mr: 2
            }}
          >
            Foreclosure Guide
          </Button>
          <Button 
            href="https://gamma.app/docs/New-Jersey-Real-Estate-Market-Analysis-f25ggs8gunle6k0?mode=doc"
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined" 
            size="large"
            sx={{ 
              color: '#1B5E20',
              borderColor: '#1B5E20',
              fontWeight: 600,
              px: 4
            }}
          >
            Market Analysis
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default InvestmentBasics;

